import React, { useEffect, useState, useRef } from 'react';
import {
	Row,
	Col,
	Checkbox,
	Modal,
	Button,
	notification,
	Card,
	Progress,
	Popconfirm,
	Icon,
	Select,
	Alert,
	Form,
} from 'antd';
import { request } from '../../utils/http';
import CacheItem from '../../components/cacheItem';
import HelpTooltip from '../../components/helpTooltip';
import { clearAllRedisCache, getRedisMemoryUsage } from '../../utils/api';

/**
 * Add new objects to the caches array below to render cache blocks
 *
 * Example:
 *	{
 *		name: 'Tema cache',						<- Name
 *		countUrl: '/path/to/get/cache/count',	<- Path to perform GET count
 *		resetUrl: '/path/to/delete/cache',		<- Path to perform DELETE request
 *		selectDbs: true|false, 					<- Show db selection in modal (Optional)
 *		selectCategories: true|false, 			<- Show category selection in modal (Optional)
 *		selectionCountUrl: '/path/to/count',	<- Path to count keys the selection would clear (Optional)
 *		staffOnly: true|false,					<- Only affects staff, so clearing it never warns (Optional)
 *		description: 'Example description',		<- Description to show in modal (Optional)
 *		info: 'What this cache holds',			<- Tooltip next to the cache name (Optional)
 *	},
 */

const caches = [
	{
		name: 'Tema cache',
		countUrl: '/admin/lawreports/categories/cache/count',
		resetUrl: '/admin/lawreports/categories/cache/filtered',
		selectionCountUrl: '/admin/lawreports/categories/cache/filtered/count',
		info: 'Sparade temalistor för rättsfall som kunderna bläddrar i. Rensade sidor byggs om mot databasen när någon öppnar dem igen.',
		selectDbs: true,
		selectCategories: true,
	},
	{
		name: 'Kund cache',
		countUrl: '/admin/organization/cache/count',
		resetUrl: '/admin/organization/cache',
		selectDbs: false,
		staffOnly: true,
		info: 'Sparad lista över kunder (organisationer) som används i admin. Påverkar bara personal.',
	},
];

// Each cleared key is rebuilt against the database, so load follows the absolute count
const WIDE_CLEAR_KEYS = 250;

const WIDE_CLEAR_DESCRIPTION = 'Bred rensning. Alla dessa sidor byggs om mot databasen vid nästa besök.';
const WIDE_CLEAR_PEAK_DESCRIPTION =
	'Det är högtrafik (vardagar 09–16). En bred rensning gör att alla dessa sidor byggs om mot databasen samtidigt, vilket kan överbelasta den. Vänta till efter kl. 16 om det går.';
const COUNT_FAILED_DESCRIPTION = 'Rensningen behandlas som bred och kräver extra bekräftelse.';
const CLEAR_ALL_CONFIRM = 'Är du säker att du vill rensa ALL cache?';
const CLEAR_ALL_PEAK_CONFIRM =
	'Det är högtrafik (vardagar 09–16). Att rensa ALL cache gör att allt byggs om mot databasen samtidigt och kan överbelasta den. Rensa ändå?';

const isPeakHours = (now = new Date()) => {
	const weekday = now.getDay() >= 1 && now.getDay() <= 5;
	return weekday && now.getHours() >= 9 && now.getHours() < 16;
};

const isFullClear = (cache) => !cache.selectCategories && !cache.selectDbs;

// request() throws on HTTP errors; this page treats a missing result as failure
const orNull = (promise) => promise.catch(() => null);

const MaintenancePage = ({}) => {
	const [dbs, setDbs] = useState(null);
	const [categories, setCategories] = useState([]);
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [includeRoot, setIncludeRoot] = useState(false);
	const [clearIndex, setClearIndex] = useState(null);
	const [modalVisible, setModalVisible] = useState(false);
	const [modalButtonLoading, setModalButtonLoading] = useState(false);
	const [redisData, setRedisData] = useState(null);
	const [selectionCount, setSelectionCount] = useState(null);
	const [selectionTouched, setSelectionTouched] = useState(false);

	const cacheItemRefs = useRef([]);

	const updateDbs = async () => {
		const result = await orNull(request('GET', '/admin/config'));

		if (result && result.databases) {
			// Filter out options that are not used.
			const newDbs = result.databases
				.map((database) => {
					if (!['Alla lagar', 'ÄHO utveckling'].includes(database.name)) {
						return {
							key: database.key,
							name: database.name,
							selected: false,
						};
					}
				})
				.filter((database) => database !== undefined);

			setDbs(newDbs);
		}
	};

	const updateCategories = async () => {
		const result = await orNull(request('GET', '/lawreports/categories?searchType=1&skipCache=true&includeHidden=true'));

		if (result && result.entries) {
			setCategories(result.entries);
		}
	};

	const selectedDbKeys = () => dbs.filter((db) => db.selected).map((db) => db.key);

	const categoryCacheQuery = () =>
		[
			...selectedDbKeys().map((key) => `databases=${key}`),
			...selectedCategories.map((id) => `categories=${id}`),
			`includeRoot=${includeRoot}`,
		].join('&');

	const hasCategorySelection = () => selectedCategories.length > 0 || includeRoot;

	const hasDbSelection = () => selectedDbKeys().length > 0;

	const hasValidSelection = () => hasCategorySelection() && hasDbSelection();

	// Errors wait for the first change so the modal doesn't open in an error state
	const requiredFieldStatus = (isFilled, message) =>
		selectionTouched && !isFilled ? { validateStatus: 'error', help: message } : {};

	const isSubmitDisabled = () => {
		const cache = caches[clearIndex];

		if (cache.selectCategories && !hasValidSelection()) {
			return true;
		}

		if (cache.selectionCountUrl) {
			return selectionCount === null || selectionCount.matched === 0;
		}

		return false;
	};

	const isWideClear = () => {
		const cache = caches[clearIndex];

		if (cache.staffOnly) {
			return false;
		}

		if (isFullClear(cache)) {
			return true;
		}

		if (selectionCount === null) {
			return false;
		}

		// Unknown scope is treated as wide so the confirmation still applies
		return selectionCount.failed || selectionCount.matched >= WIDE_CLEAR_KEYS;
	};

	const updateSelectionCount = async (cache, isCurrent) => {
		const [matched, total] = await Promise.all([
			orNull(request('GET', `${cache.selectionCountUrl}?${categoryCacheQuery()}`)),
			orNull(request('GET', cache.countUrl)),
		]);

		if (!isCurrent()) {
			return;
		}

		if (typeof matched !== 'number' || typeof total !== 'number') {
			setSelectionCount({ failed: true });
			return;
		}

		// Keys can be added between the two calls
		setSelectionCount({ matched, total: Math.max(matched, total) });
	};

	const toggleDb = (index) => {
		const newDbs = [...dbs];
		newDbs[index].selected = !newDbs[index].selected;
		setDbs(newDbs);
		setSelectionTouched(true);
	};

	const toggleAllDbs = (selected) => {
		const newDbs = dbs.map((db) => ({
			...db,
			selected,
		}));
		setDbs(newDbs);
	};

	const changeSelectedDbs = (selected) => {
		toggleAllDbs(selected);
		setSelectionTouched(true);
	};

	const changeSelectedCategories = (ids) => {
		setSelectedCategories(ids);
		setSelectionTouched(true);
	};

	const changeIncludeRoot = (checked) => {
		setIncludeRoot(checked);
		setSelectionTouched(true);
	};

	const changeAllCategories = (selected) => {
		changeSelectedCategories(selected ? categories.map((category) => category.id) : []);
		changeIncludeRoot(selected);
	};

	const handleClearClick = (index) => {
		setClearIndex(index);
		setModalVisible(true);
	};

	const resetModal = () => {
		setModalVisible(false);
		setClearIndex(null);
		toggleAllDbs(false);
		setSelectedCategories([]);
		setIncludeRoot(false);
		setSelectionCount(null);
		setSelectionTouched(false);
	};

	const getClearUrl = (cache) => {
		if (cache.selectCategories) {
			return `${cache.resetUrl}?${categoryCacheQuery()}`;
		}

		const dbString = cache.selectDbs ? selectedDbKeys().join('') : '';

		return dbString ? `${cache.resetUrl}?databases=${dbString}` : cache.resetUrl;
	};

	const handleSubmitModal = async () => {
		setModalButtonLoading(true);
		const cache = caches[clearIndex];

		const result = await orNull(request('DELETE', getClearUrl(cache)));
		setModalButtonLoading(false);

		if (result) {
			notification.success({
				message: `${cache.name} rensad.`,
			});

			// Trigger update for all CacheItems
			cacheItemRefs.current.forEach((ref) => ref.updateCacheCount());
		} else {
			notification.error({
				message: `Rensning av ${cache.name.toLowerCase()} misslyckades.`,
			});
		}

		resetModal();
	};

	const getRedisMemoryData = async () => {
		const result = await orNull(getRedisMemoryUsage());

		if (result) {
			setRedisData(result);
		}

		// Trigger update for all CacheItems
		cacheItemRefs.current.forEach((ref) => ref.updateCacheCount());
	};

	const clearAllCache = async () => {
		const result = await orNull(clearAllRedisCache());

		if (result) {
			notification.success({
				message: `All redis cache rensad.`,
			});

			// Update redis data
			getRedisMemoryData();

			// Trigger update for all CacheItems
			cacheItemRefs.current.forEach((ref) => ref.updateCacheCount());
		} else {
			notification.error({
				message: `Rensning av all redis cache misslyckades.`,
			});
		}
	};

	useEffect(() => {
		if (!dbs) {
			updateDbs();
		}

		if (!redisData) {
			getRedisMemoryData();
		}

		if (categories.length === 0) {
			updateCategories();
		}
	}, [dbs]);

	useEffect(() => {
		setSelectionCount(null);

		const cache = clearIndex !== null ? caches[clearIndex] : null;

		if (!cache || !cache.selectionCountUrl || !hasValidSelection()) {
			return;
		}

		let current = true;
		updateSelectionCount(cache, () => current);

		return () => {
			current = false;
		};
	}, [clearIndex, dbs, selectedCategories, includeRoot]);

	const getScopeMessage = (cache) => {
		if (isFullClear(cache)) {
			return `Rensar hela ${cache.name.toLowerCase()}.`;
		}

		if (selectionCount === null) {
			return null;
		}

		return `Rensar ${selectionCount.matched} av ${selectionCount.total} nycklar.`;
	};

	const getClearScope = () => {
		const cache = caches[clearIndex];

		if (cache.staffOnly) {
			return { type: 'info', message: `Rensar hela ${cache.name.toLowerCase()}. Påverkar bara personal.` };
		}

		if (selectionCount !== null && selectionCount.failed) {
			return { type: 'warning', message: 'Kunde inte räkna hur mycket som rensas.', description: COUNT_FAILED_DESCRIPTION };
		}

		if (selectionCount !== null && selectionCount.matched === 0) {
			return { type: 'info', message: 'Inga nycklar matchar urvalet. Det finns inget att rensa.' };
		}

		const message = getScopeMessage(cache);

		if (!message) {
			return null;
		}

		if (!isWideClear()) {
			return { type: 'info', message };
		}

		return isPeakHours()
			? { type: 'error', message, description: WIDE_CLEAR_PEAK_DESCRIPTION }
			: { type: 'warning', message, description: WIDE_CLEAR_DESCRIPTION };
	};

	const renderClearScope = () => {
		const scope = getClearScope();

		return scope && <Alert style={{ marginTop: 24 }} showIcon {...scope} />;
	};

	// Rendered inside the field label, so the Form.Item needs htmlFor or clicking the title presses "Välj alla"
	const renderSelectAllButtons = (onChange) => (
		<span style={{ float: 'right' }}>
			<Button size='small' onClick={() => onChange(true)}>
				Välj alla
			</Button>
			<Button size='small' onClick={() => onChange(false)} style={{ marginLeft: 6 }}>
				Töm
			</Button>
		</span>
	);

	const renderSubmitButton = () => {
		const button = (
			<Button
				key='submit'
				type='danger'
				icon='delete'
				loading={modalButtonLoading}
				disabled={isSubmitDisabled()}
				onClick={isWideClear() ? undefined : handleSubmitModal}
			>
				Rensa
			</Button>
		);

		if (!isWideClear() || isSubmitDisabled()) {
			return button;
		}

		return (
			<Popconfirm
				key='submit'
				title='Det här är en bred rensning. Är du säker?'
				onConfirm={handleSubmitModal}
				okText='Ja, rensa'
				cancelText='Nej'
				okType='danger'
				icon={<Icon type='warning' style={{ color: 'red' }} />}
			>
				{button}
			</Popconfirm>
		);
	};

	return (
		<Row gutter={24}>
			{isPeakHours() && (
				<Col span={24} style={{ marginBottom: 24 }}>
					<Alert
						type='warning'
						showIcon
						message='Högtrafik (vardagar 09–16)'
						description='Rensa så smalt som möjligt just nu. Breda rensningar gör att många sidor byggs om mot databasen samtidigt och kan överbelasta den.'
					/>
				</Col>
			)}
			{dbs && clearIndex !== null && (
				<Modal
					title={`Rensa ${caches[clearIndex].name.toLowerCase()}`}
					visible={modalVisible}
					maskClosable={true}
					onCancel={resetModal}
					footer={[
						<Button key='back' onClick={resetModal}>
							Avbryt
						</Button>,
						renderSubmitButton(),
					]}
				>
					<Form layout='vertical'>
						{caches[clearIndex].selectCategories && (
							<Form.Item
								required
								htmlFor='teman'
								label={
									<span>
										Teman
										<HelpTooltip title='Använd när rättsfall i ett tema har lagts till, ändrats eller tagits bort. Allt under valt tema rensas.' />
										{renderSelectAllButtons(changeAllCategories)}
									</span>
								}
								{...requiredFieldStatus(hasCategorySelection(), 'Välj minst ett tema eller Temalistan (toppnivå)')}
							>
								<Select
									id='teman'
									mode='multiple'
									allowClear
									placeholder='Välj teman'
									optionFilterProp='children'
									maxTagCount={6}
									maxTagPlaceholder={(omitted) => `+${omitted.length} till`}
									value={selectedCategories}
									onChange={changeSelectedCategories}
									style={{ width: '100%' }}
								>
									{categories.map((category) => (
										<Select.Option key={category.id} value={category.id}>
											{category.name}
										</Select.Option>
									))}
								</Select>
								<Checkbox checked={includeRoot} onChange={(e) => changeIncludeRoot(e.target.checked)}>
									Temalistan (toppnivå)
								</Checkbox>
								<HelpTooltip title='Använd när ett tema på toppnivå har lagts till, tagits bort, bytt namn eller dolts/visats.' />
							</Form.Item>
						)}
						{caches[clearIndex].hasOwnProperty('selectDbs') && caches[clearIndex].selectDbs && (
							<Form.Item
								required
								htmlFor='databaser'
								label={
									<span>
										Databaser
										<HelpTooltip title='Välj de databaser där ändringen ska synas. Är du osäker, välj alla.' />
										{renderSelectAllButtons(changeSelectedDbs)}
									</span>
								}
								{...requiredFieldStatus(hasDbSelection(), 'Välj minst en databas')}
							>
								<Row>
									{dbs.map((db, index) => (
										<Col span={12} key={index}>
											<Checkbox
												checked={db.selected}
												onChange={() => toggleDb(index)}
												value={db.name}
											>
												{db.name}
											</Checkbox>
										</Col>
									))}
								</Row>
							</Form.Item>
						)}
					</Form>
					{!caches[clearIndex].selectDbs && caches[clearIndex].description && (
						<p>{caches[clearIndex].description}</p>
					)}
					{renderClearScope()}
				</Modal>
			)}

			{redisData && (
				<Col span={24} style={{ marginBottom: 24 }}>
					<Card
						title={
							<span>
								Redis Status
								<HelpTooltip title='Rensa allt tömmer all cache i Redis: Tema cache, Kund cache och AI-inställningar. Synkmarkörer och RAG-indexets tidsstämplar sparas, så synkar fortsätter där de var.' />
							</span>
						}
						extra={[
							<Popconfirm
								title={isPeakHours() ? CLEAR_ALL_PEAK_CONFIRM : CLEAR_ALL_CONFIRM}
								onConfirm={clearAllCache}
								okText='Ja'
								cancelText='Nej'
								okType='danger'
								okButtonProps={{ size: 'default' }}
								cancelButtonProps={{ size: 'default' }}
								placement='bottom'
								icon={<Icon type='delete' style={{ color: 'red' }} />}
							>
								<Button type='danger' size='default' icon='delete' style={{ marginRight: 12 }}>
									Rensa allt
								</Button>
							</Popconfirm>,
							<Button type='primary' size='default' icon='reload' onClick={getRedisMemoryData}>
								Uppdatera
							</Button>,
						]}
					>
						<Row>
							<Col xs={24} sm={24} md={12} lg={16} xl={16}>
								<Row>
									<p>
										<b>Nuvarande minnesanvändning:</b> {redisData.used_memory_human}B
									</p>
								</Row>
								<Row>
									<p>
										<b>Högsta minnesanvändning:</b> {redisData.used_memory_peak_human}B
									</p>
								</Row>
								<Row>
									<p>
										<b>Tillgängligt minne:</b> {redisData.maxmemory_human}B
									</p>
								</Row>
							</Col>
							<Col xs={12} sm={12} md={6} lg={4} xl={4}>
								<Row justify='center' type='flex'>
									<Progress
										type='dashboard'
										percent={((redisData.used_memory / redisData.maxmemory) * 100).toFixed(2)}
										status={
											(redisData.used_memory / redisData.maxmemory) * 100 > 100
												? 'exception'
												: 'active'
										}
									/>
								</Row>
								<Row justify='center' type='flex'>
									<h2>Nuvarande</h2>
								</Row>
							</Col>
							<Col xs={12} sm={12} md={6} lg={4} xl={4}>
								<Row justify='center' type='flex'>
									<Progress
										type='dashboard'
										percent={((redisData.used_memory_peak / redisData.maxmemory) * 100).toFixed(2)}
										status={
											(redisData.used_memory_peak / redisData.maxmemory) * 100 > 100
												? 'exception'
												: 'active'
										}
									/>
								</Row>
								<Row justify='center' type='flex'>
									<h2>Högsta</h2>
								</Row>
							</Col>
						</Row>
					</Card>
				</Col>
			)}

			<Col xl={24} style={{ marginBottom: 8 }}>
				<Row type='flex' justify='start' gutter={12}>
					{caches.map((cache, index) => (
						<Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={6} style={{ marginBottom: 12 }}>
							<CacheItem
								ref={(el) => (cacheItemRefs.current[index] = el)}
								key={index}
								name={cache.name}
								info={cache.info}
								label={cache.label}
								countUrl={cache.countUrl}
								onClear={() => handleClearClick(index)}
							/>
						</Col>
					))}
				</Row>
			</Col>
		</Row>
	);
};

export default MaintenancePage;



// WEBPACK FOOTER //
// ./src/pages/maintenance/maintenancePage.jsx